import { UserType } from '../../services/checkout-bff/campaign-conditions';
import { UserType as UserTypeParam } from '../types/user';

export interface UserMatchContext {
    isWinback?: boolean;
    isBeginner?: boolean;
    isAmbassador?: boolean;
}

const AMBASSADOR: UserType = 'ambassador_appointed';

// Fonte única da regra "o que é um ambassador": presença do imb_id na URL.
export const getIsAmbassador = (query?: {
    imb_id?: string | string[];
}): boolean => !!query?.imb_id;

// Segmento de usuário vindo da URL (?user_type=beginner). Usa o enum de
// utils/types/user (beginner/winback/ambassador/default) — distinto do UserType
// da campanha. É um override de segmento por query.
export const getUserTypeParam = (query?: {
    user_type?: string | string[];
}): UserTypeParam | undefined =>
    query?.user_type?.toString() as UserTypeParam | undefined;

// Deriva o contexto do usuário a partir das fontes canônicas:
// - ambassador vem da query (imb_id);
// - winback/beginner vêm do order (resolvidos na verificação de e-mail).
export const getUserContext = ({
    query,
    order,
}: {
    query?: { imb_id?: string | string[] };
    order?: { isWinback?: boolean; isBeginner?: boolean };
}): UserMatchContext => ({
    isAmbassador: getIsAmbassador(query),
    isWinback: order?.isWinback,
    isBeginner: order?.isBeginner,
});

// Uma regra casa quando TODOS os tipos listados são verdadeiros para o contexto
// do usuário (AND). Lista vazia/ausente aplica a todos.
export const matchesUserType = (
    userTypes: UserType[] | undefined,
    userContext: UserMatchContext
): boolean => {
    if (!userTypes || userTypes.length === 0) {
        return true;
    }

    // If the rule explicitly pairs ambassador_appointed with another type
    // (e.g. ['ambassador_appointed','winback']), treat it as an AND: both
    // flags must be present. For other multi-value lists (e.g. ['new','beginner'])
    // treat them as OR: the rule matches when ANY listed type applies.
    const includesAmbassador = userTypes.includes('ambassador_appointed');

    const checkSingle = (userType: UserType): boolean => {
        switch (userType) {
            case 'ambassador_appointed':
                return userContext.isAmbassador === true;
            case 'winback':
                return userContext.isWinback === true;
            case 'beginner':
                return userContext.isBeginner === true;
            case 'new':
                return (
                    userContext.isAmbassador !== true &&
                    userContext.isWinback !== true &&
                    userContext.isBeginner !== true
                );
            default:
                return false;
        }
    };

    if (includesAmbassador && userTypes.length > 1) {
        // AND semantics
        return userTypes.every(checkSingle);
    }

    // OR semantics
    return userTypes.some(checkSingle);
};

// Resolve o usuário a UMA única role, na MESMA prioridade do lambda-new-checkout
// (Object.keys({isAmbassador, isBeginner, isWinback}).find) para o front casar
// com o que o BFF recebe: ambassador > beginner > winback > new.
export const resolveUserType = (ctx: UserMatchContext): UserType => {
    if (ctx.isAmbassador) return AMBASSADOR;
    if (ctx.isBeginner) return 'beginner';
    if (ctx.isWinback) return 'winback';
    return 'new';
};
